// TanStack Router's `redirect()` returns a special Redirect object that the
// framework REQUIRES to be thrown from beforeLoad to trigger a navigation; it
// is the documented, idiomatic control-flow mechanism (not an Error). The rule
// cannot see these are framework redirects, so it is disabled for this module.
/* eslint-disable @typescript-eslint/only-throw-error */
import { Outlet, createRootRouteWithContext, createRoute, redirect } from '@tanstack/react-router';

import { AppShellLayout } from '@/components/app-shell/AppShellLayout';
import { NAV_ITEMS, SETTINGS_ROLES } from '@/components/app-shell/nav-items';
import { UnassignedState } from '@/components/app-shell/UnassignedState';
import { NlqParityHarness } from '@/dev/NlqParityHarness';
import { PrimitivesParityHarness } from '@/dev/PrimitivesParityHarness';
import { AuthCallbackPage } from '@/features/auth/AuthCallbackPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { CustomersPage } from '@/features/admin/CustomersPage';
import { UserActivityPage } from '@/features/admin/UserActivityPage';
import { AskBankIQPage } from '@/features/ask-bankiq/AskBankIQPage';
import { IngestionPage } from '@/features/ingestion/IngestionPage';
import { VaultPage } from '@/features/vault/VaultPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import type { WizardInit } from '@/features/campaigns/wizard/campaign-state';
import {
  EMPTY_COLUMN_FILTERS,
  type ColumnFilters,
} from '@/features/campaigns/ledger/ledger-filter';
import type { InsightType } from '@/features/intelligence-dashboard/insight.types';

import { CampaignsRoute } from './route-campaigns';
import { DashboardRoute } from './route-dashboard';
import { ReferralsRoute } from './route-referrals';
import { type RouterContext, safeRedirectPath } from './auth-context';
import { NAV_ID_TO_PATH, type NavId, canRoleAccessNavId, roleLandingPath } from './paths';

/**
 * SCRUM-616 — code-based TanStack Router tree.
 *
 * Design (see plan D1/D2/D6): a pathless authed LAYOUT route holds the app
 * chrome (TopNav + SideNav + <Outlet/>) behind a single fail-closed beforeLoad
 * guard; /login and /auth/callback sit OUTSIDE it; the index route redirects to
 * the role landing. Auth is read from context.auth (injected from the in-memory
 * store — no persistence, token-off-disk preserved).
 *
 * Per-app-route beforeLoad adds CLIENT-SIDE role parity (canRoleAccessNavId):
 * a hand-typed URL a role can't see in the nav redirects to its landing. This
 * mirrors the pre-router nav hiding; real server authz is SCRUM-103.
 */

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

// ── Public routes (outside the authed layout) ─────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const safe = safeRedirectPath(search.redirect);
    return safe ? { redirect: safe } : {};
  },
  beforeLoad: ({ context, search }) => {
    if (context.auth.isAuthenticated) {
      const landing = roleLandingPath(context.auth.role);
      throw redirect({ to: search.redirect ?? landing ?? NAV_ID_TO_PATH.dashboard });
    }
  },
  component: LoginPage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  // Matches CALLBACK_PATH ('/auth/callback') from features/auth/oidc.ts.
  path: '/auth/callback',
  component: AuthCallbackPage,
});

// ── Authed layout + index redirect ────────────────────────────────────────

const authedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      // Fail-closed: no in-memory session -> login, preserving intended path.
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppShellLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) throw redirect({ to: '/login' });
    const landing = roleLandingPath(context.auth.role);
    // Unassigned has no landing -> send to the authed layout root, which renders
    // the pending-access state.
    throw redirect({ to: landing ?? '/unassigned' });
  },
});

/**
 * Shared role-visibility guard for an app route (SCRUM-616, plan D6). Redirects
 * a user to their landing if their role can't see this nav id — client-side
 * parity with SideNav visibility, NOT server-side authorization (SCRUM-103).
 * Returned as a plain beforeLoad fn so each route keeps its literal path type
 * (a generic wrapper broke TanStack's typed-navigation inference).
 */
function roleGuard(navId: NavId) {
  return ({ context }: { context: RouterContext }) => {
    if (!canRoleAccessNavId(context.auth.role, navId, NAV_ITEMS, SETTINGS_ROLES)) {
      const landing = roleLandingPath(context.auth.role);
      throw redirect({ to: landing ?? '/unassigned' });
    }
  };
}

// ── App routes ────────────────────────────────────────────────────────────

const dashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH.dashboard,
  beforeLoad: roleGuard('dashboard'),
  component: DashboardRoute,
});

const referralsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH.referrals,
  validateSearch: (search: Record<string, unknown>): { scope?: 'mine' | 'all' } => {
    const raw = search.scope;
    return raw === 'mine' || raw === 'all' ? { scope: raw } : {};
  },
  beforeLoad: roleGuard('referrals'),
  component: ReferralsRoute,
});

/** Typed search for the Campaigns deep-links (SCRUM-261/327/348, plan D4). */
export interface CampaignsSearch {
  inspect?: string;
  wizard?: boolean;
  targets?: string[];
  product?: string;
  productPreset?: boolean;
  campaignName?: string;
  step?: number;
  linkedReferral?: string;
  // SCRUM-847: Dashboard KPI drill-through. A single insight signal (Potential Revenue → relgap,
  // Deposits at Risk → flight) or the last-30-days activity filter (Customers Reached → reached),
  // seeded into the ledger's column filters on mount and then cleared from the URL.
  insight?: InsightType;
  reached?: boolean;
}

/** The insight signals a Dashboard KPI card can drill into (SCRUM-847). */
const KPI_INSIGHT_KEYS: readonly InsightType[] = ['relgap', 'flight', 'dormant', 'smb'];

const campaignsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH.campaigns,
  validateSearch: (search: Record<string, unknown>): CampaignsSearch => {
    const out: CampaignsSearch = {};
    if (typeof search.inspect === 'string') out.inspect = search.inspect;
    if (search.wizard === true || search.wizard === 'true') out.wizard = true;
    if (Array.isArray(search.targets)) {
      out.targets = search.targets.filter((t): t is string => typeof t === 'string');
    }
    if (typeof search.product === 'string') out.product = search.product;
    if (search.productPreset === true || search.productPreset === 'true') {
      out.productPreset = true;
    }
    if (typeof search.campaignName === 'string') out.campaignName = search.campaignName;
    if (typeof search.step === 'number') out.step = search.step;
    else if (typeof search.step === 'string' && search.step.trim() !== '') {
      const n = Number(search.step);
      if (Number.isFinite(n)) out.step = n;
    }
    if (typeof search.linkedReferral === 'string') out.linkedReferral = search.linkedReferral;
    // SCRUM-847: KPI drill-through params. Enum-validate `insight`; ignore anything unknown.
    if (
      typeof search.insight === 'string' &&
      (KPI_INSIGHT_KEYS as readonly string[]).includes(search.insight)
    ) {
      out.insight = search.insight as InsightType;
    }
    if (search.reached === true || search.reached === 'true') out.reached = true;
    return out;
  },
  beforeLoad: roleGuard('campaigns'),
  component: CampaignsRoute,
});

const tellerDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH['teller-dashboard'],
  beforeLoad: roleGuard('teller-dashboard'),
  component: () => <CampaignsRoute tellerMode />,
});
const vaultRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH.vault,
  beforeLoad: roleGuard('vault'),
  component: VaultPage,
});
const customerDashboardRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH['customer-dashboard'],
  beforeLoad: roleGuard('customer-dashboard'),
  component: CustomersPage,
});
const userActivityRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH['user-activity'],
  beforeLoad: roleGuard('user-activity'),
  component: UserActivityPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH.settings,
  beforeLoad: roleGuard('settings'),
  component: SettingsPage,
});
const askBankiqRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH['ask-bankiq'],
  beforeLoad: roleGuard('ask-bankiq'),
  component: AskBankIQPage,
});
const ingestionRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: NAV_ID_TO_PATH.ingestion,
  beforeLoad: roleGuard('ingestion'),
  component: IngestionPage,
});

/** Pending-access state for the Unassigned role (no landing). */
const unassignedRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/unassigned',
  component: UnassignedState,
});

/** Helper for producers building a Campaigns deep-link from a WizardInit. */
export function wizardInitToCampaignsSearch(init: WizardInit): CampaignsSearch {
  const out: CampaignsSearch = { wizard: true };
  if (init.targetIds && init.targetIds.length > 0) out.targets = init.targetIds;
  if (init.productId) out.product = init.productId;
  if (init.productPreSelected) out.productPreset = true;
  if (init.campaignName) out.campaignName = init.campaignName;
  if (typeof init.step === 'number') out.step = init.step;
  if (init.linkedReferralId) out.linkedReferral = init.linkedReferralId;
  return out;
}

/** Rebuild a WizardInit from the Campaigns search params (consumer side). */
export function campaignsSearchToWizardInit(search: CampaignsSearch): WizardInit | null {
  if (!search.wizard) return null;
  const init: WizardInit = {};
  if (search.targets) init.targetIds = search.targets;
  if (search.product) init.productId = search.product;
  if (search.productPreset) init.productPreSelected = true;
  if (search.campaignName) init.campaignName = search.campaignName;
  if (typeof search.step === 'number') init.step = search.step;
  if (search.linkedReferral) init.linkedReferralId = search.linkedReferral;
  return init;
}

/**
 * SCRUM-847: what a Dashboard KPI card drills into. `'reached'` is the last-30-days activity
 * population (Customers Reached); the rest are insight signals.
 */
export type KpiDrillTarget = InsightType | 'reached';

/** Producer: build a Campaigns deep-link from a KPI drill target (Dashboard side). */
export function kpiDrillToCampaignsSearch(target: KpiDrillTarget): CampaignsSearch {
  return target === 'reached' ? { reached: true } : { insight: target };
}

/**
 * Consumer: turn the KPI drill search params into a fresh ColumnFilters to seed the ledger.
 * `insight` → a single `signals` column filter; `reached` → `last: { max: 30 }` (the same
 * `daysAgo <= 30` population dashboardMetrics counts as "reached in the last 30 days"). Returns
 * null when no KPI param is present.
 */
export function campaignsSearchToInitialColumnFilters(
  search: CampaignsSearch,
): ColumnFilters | null {
  if (search.insight) {
    return { ...EMPTY_COLUMN_FILTERS, signals: search.insight };
  }
  if (search.reached) {
    return { ...EMPTY_COLUMN_FILTERS, last: { max: 30 } };
  }
  return null;
}

const appChildren = [
  dashboardRoute,
  referralsRoute,
  campaignsRoute,
  tellerDashboardRoute,
  vaultRoute,
  customerDashboardRoute,
  userActivityRoute,
  settingsRoute,
  askBankiqRoute,
  ingestionRoute,
  unassignedRoute,
];

/**
 * Dev-only parity harness routes (SCRUM-83/457). They self-seed the auth store
 * and render outside the authed layout, so they sit at the root. Added to the
 * tree ONLY under import.meta.env.DEV — Vite dead-code-eliminates the harness
 * imports (and these routes) from production builds, preserving SCRUM-616 SC9.
 */
const devParityRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/__parity',
        component: PrimitivesParityHarness,
      }),
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/__parity/nlq',
        component: NlqParityHarness,
      }),
    ]
  : [];

export const routeTree = rootRoute.addChildren([
  loginRoute,
  callbackRoute,
  indexRoute,
  authedLayoutRoute.addChildren(appChildren),
  ...devParityRoutes,
]);

export { loginRoute, callbackRoute, authedLayoutRoute, indexRoute, campaignsRoute, referralsRoute };
